// src/components/Forms/CourseOrderForm/InlineLogin.jsx
import React, { useState } from "react";
import { loginBuyer } from "../../../utils/coursesClient";

export default function InlineLogin({
  email,
  onLoginSuccess,
  onCancel,
  qa_mode = false,
  proxyUrl = null,
}) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const forgotPasswordUrl = qa_mode
    ? `${import.meta.env.VITE_COURSES_APP_STAGING_URL || "https://unclekam-courses-app-staging.up.railway.app"}/auth/forgot-password`
    : `${import.meta.env.VITE_COURSES_APP_PROD_URL || "https://courses.unclekam.com"}/auth/forgot-password`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) {
      setError("Please enter your password.");
      return;
    }
    setError("");
    setLoading(true);

    try {
      const res = await loginBuyer(
        { email: email.trim(), password },
        { qa_mode, proxyUrl }
      );
      const token = res?.token || res?.data?.token;
      const user = res?.user || res?.data?.user;

      if (token && user) {
        onLoginSuccess(token, user);
      } else {
        setError("Invalid response from server. Please try again.");
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error?.message ||
        "Invalid email or password. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="osof-login-panel my-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="fw-bold mb-0 text-dark">Welcome Back! Log In</h6>
        {onCancel && (
          <button
            type="button"
            className="btn btn-sm btn-link text-muted p-0 text-decoration-none"
            onClick={onCancel}
          >
            ✕ Cancel
          </button>
        )}
      </div>
      <p className="text-muted small mb-3">
        An account already exists for <strong>{email}</strong>. Enter your password to
        complete this purchase with your existing account.
      </p>

      {error && (
        <div className="alert alert-danger py-2 px-3 small" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label small fw-semibold">Email Address</label>
          <input
            type="email"
            className="form-control osof-input"
            value={email}
            disabled
            readOnly
          />
        </div>

        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <label className="form-label small fw-semibold mb-0">Password</label>
            <a
              href={forgotPasswordUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="small text-decoration-none text-primary"
            >
              Forgot password?
            </a>
          </div>
          <div className="osof-password-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              className="form-control osof-input"
              placeholder="Enter your account password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              required
            />
            <button
              type="button"
              className="osof-password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div className="d-flex gap-2">
          <button
            type="submit"
            className="btn btn-primary osof-btn flex-grow-1 py-2 fw-semibold"
            disabled={loading}
          >
            {loading ? <span className="osof-loader" /> : "Log In & Continue"}
          </button>
          {onCancel && (
            <button
              type="button"
              className="btn btn-outline-secondary py-2"
              onClick={onCancel}
              disabled={loading}
            >
              Change Email
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
